/**
 * Export Button Component
 * v4.0 - Provides export functionality for analytics reports
 */

import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Download,
  TableChart,
  Description,
  Code,
  PictureAsPdf
} from '@mui/icons-material';
import { exportData, prepareDataForExport, ExportFormat } from '../../services/exportService';

interface ExportButtonProps {
  data: any[];
  filename: string;
  metadata?: {
    dateRange?: { startDate: Date; endDate: Date };
    filters?: Record<string, any>;
  };
  disabled?: boolean;
  formats?: ExportFormat[];
}

export const ExportButton: React.FC<ExportButtonProps> = ({
  data,
  filename,
  metadata,
  disabled = false,
  formats = ['csv', 'excel', 'json']
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (data.length === 0) {
      setSnackbar({
        open: true,
        message: 'No data available to export',
        severity: 'error'
      });
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExport = async (format: ExportFormat) => {
    handleClose();
    setLoading(true);

    try {
      // Prepare data for export
      const exportData_prepared = prepareDataForExport(data);

      // Export
      await exportData({
        filename,
        data: exportData_prepared,
        format,
        metadata
      });

      setSnackbar({
        open: true,
        message: `Successfully exported ${data.length} records as ${format.toUpperCase()}`,
        severity: 'success'
      });
    } catch (error: any) {
      console.error('Export error:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to export data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const formatIcons: Record<ExportFormat, React.ReactNode> = {
    csv: <TableChart fontSize="small" />,
    excel: <Description fontSize="small" />,
    json: <Code fontSize="small" />,
    pdf: <PictureAsPdf fontSize="small" />
  };

  const formatLabels: Record<ExportFormat, string> = {
    csv: 'Export as CSV',
    excel: 'Export as Excel',
    json: 'Export as JSON',
    pdf: 'Export as PDF'
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={loading ? <CircularProgress size={16} /> : <Download />}
        onClick={handleClick}
        disabled={disabled || loading || data.length === 0}
        size="small"
      >
        Export
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {formats.map((format) => (
          <MenuItem key={format} onClick={() => handleExport(format)}>
            <ListItemIcon>
              {formatIcons[format]}
            </ListItemIcon>
            <ListItemText>{formatLabels[format]}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ExportButton;
